import { can } from "./permissions";
import { findPageRule, hasArea, levelOf, levelRank, type AccessLevel } from "./access";
import type { CurrentUser } from "./types";

/** HRMS pages are open to exactly the people whose user profile (Settings → Users → Edit → Module
 *  access) has HRMS ticked — no role grants it by itself, not even Super Admin / System Admin.
 *  Individual pages can still be switched off per person under Menu Access. Mirrored by
 *  canManageHr in the API's modules/hr/hr.common.ts. */
export function hasHrmsAccess(user: CurrentUser | null | undefined): boolean {
  return !!user && user.moduleAccess.includes("HRMS");
}

/**
 * Whether `user` may open the sidebar item with this MENU_REGISTRY key — the one
 * place these rules live, used both to show an item as locked in the sidebar and
 * to block its page (AppLayout), so the two can never disagree.
 *
 * Combines the role/module/permission rules with the per-user restrictions set
 * in Settings -> Users -> Edit -> Menu Access (see lib/menuRegistry.ts).
 */
export function canAccessMenu(user: CurrentUser | null | undefined, key: string): boolean {
  if (!user) return false;
  if (user.restrictedMenuKeys?.includes(key)) return false;

  const isStaff = user.roles.includes("STAFF");
  const hasModule = (m: "CRM" | "ERP" | "FINANCE" | "HRMS" | "WORKFLOW") => user.moduleAccess.includes(m);

  // Staff reach Leave/Claim (and their own profile) only — everything else is off-limits.
  if (key === "tools.request") return true;
  if (isStaff) return false;

  // Settings → Access matrix: a menu whose area is None (or below what the page needs) is hidden
  // (a page can be set on its own — for a role or one person — otherwise it follows its area)
  const gate = findPageRule(user.pageRules, key) ?? menuArea(key);
  if (gate && levelRank(levelOf(user, key)) < levelRank(gate.min)) return false;

  // The Password Manager follows the Access matrix ("Password Manager"), not the HRMS module (same as the API).
  if (key === "hrms.password-manager") return hasArea(user, "passwords", "ALL");
  // request 1254: Owner Details / Management Family Details are confidential — Password Manager access, inside HRMS
  if (key === "hrms.owner-details" || key === "hrms.management-family") return hasArea(user, "passwords", "ALL") && hasHrmsAccess(user);

  // Every HRMS page follows the person's HRMS module access (plus the Menu Access ticks above).
  if (key.startsWith("hrms.")) return hasHrmsAccess(user);

  if (key.startsWith("erp.")) return hasModule("ERP");

  // Finance has its own module tick on the user profile (localhost preview).
  if (key.startsWith("finance.")) return hasModule("FINANCE");

  switch (key) {
    case "workflow.my-tasks":
    case "tools.tickets":
    case "tools.activity":
    case "tools.tags":
      return true;
    case "workflow.team":
    case "workflow.follow-ups":
      return can(user, "VIEW_TEAM_WORKLOAD");
    case "crm.customers":
      return hasModule("CRM") && can(user, "VIEW_WORKFLOW");
    case "erp.erp":
      return hasModule("ERP");
    case "tools.time-logs":
      return can(user, "VIEW_TIME_LOGS", "TEAM");
    default:
      // workflow.enquiries / estimation / accounts / procurement / boards / history
      return key.startsWith("workflow.") ? can(user, "VIEW_WORKFLOW") : true;
  }
}

/** Which Access-matrix area each menu belongs to, and the level the page needs to be useful. */
function menuArea(key: string): { area: string; min: AccessLevel } | null {
  const exact: Record<string, { area: string; min: AccessLevel }> = {
    "workflow.enquiries": { area: "enquiries", min: "OWN" },
    "workflow.estimation": { area: "enquiries", min: "OWN" },
    "workflow.boards": { area: "projects", min: "OWN" },
    "workflow.my-tasks": { area: "projects", min: "OWN" },
    "workflow.history": { area: "projects", min: "OWN" },
    "workflow.team": { area: "projects", min: "TEAM" },
    "workflow.follow-ups": { area: "projects", min: "TEAM" },
    "workflow.amc": { area: "amc", min: "ALL" },
    "workflow.procurement": { area: "procurement", min: "ALL" },
    "workflow.accounts": { area: "finance", min: "ALL" },
    "crm.customers": { area: "crm", min: "ALL" },
    "hrms.attendance": { area: "attendance", min: "ALL" },
    "hrms.labour-attendance": { area: "attendance", min: "ALL" },
    "hrms.reports.attendance": { area: "attendance", min: "ALL" },
    "hrms.reports.leave": { area: "leaveApprove", min: "TEAM" },
    "hrms.leave-summary": { area: "leaveApprove", min: "TEAM" },
    "hrms.reports.payroll": { area: "payroll", min: "ALL" },
    "hrms.generate": { area: "hrDocs", min: "ALL" },
    "hrms.air-tickets": { area: "hrDocs", min: "ALL" },
    "hrms.password-manager": { area: "passwords", min: "ALL" },
    "hrms.owner-details": { area: "passwords", min: "ALL" },
    "hrms.management-family": { area: "passwords", min: "ALL" },
  };
  if (exact[key]) return exact[key];
  if (key.startsWith("hrms.payroll.")) return { area: "payroll", min: "ALL" };
  if (key.startsWith("hrms.")) return { area: "employees", min: "ALL" };
  if (key.startsWith("erp.")) return { area: "procurement", min: "ALL" };
  if (key.startsWith("finance.")) return { area: "finance", min: "ALL" };
  return null;
}
